import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Attr } from '@cumlaude/metadata';
import { BaseDetailsPanelComponent, convert, convertDate } from '../../base-details-panel/base-details-panel.component';
import { BasicFilterExpression, CompoundFilterExpression, FilterExpression, ListOptions, TableResponse } from '../../../services/data.service';
import { RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { DetailsElementData, ElementDataDetail, Leerling, LeerlingDetail, LijstElementData } from '../../Details';
import { isNull } from 'lodash-es';
import { Observable } from 'rxjs';
import { SvgSpinnerComponent } from '../../../layout/svg-spinner/svg-spinner.component';
import { DetailPanelComponent } from '../../detail-panel/detail-panel.component';
import { LijstComponent } from '../../lijst/lijst.component';
import { InstantSearchBoxComponent } from '@cumlaude/shared-components-inputs';
import { UserTabMenuComponent } from '../../../layout/user-tab-menu/user-tab-menu.component';
import { UserService } from '../../../services/user.service';
import { SelectionBarComponent } from '../../../filter-bar/selection-bar.component';
import { parseSearchInputToNumbers } from '@cumlaude/shared-utils';
import { ClickBlockingOverlayComponent } from '../../../layout/click-blocking-overlay/click-blocking-overlay.component';
import { DashboardActiesComponent } from '../../../layout/dashboard-acties/dashboard-acties.component';
import { TabMenuComponent } from '@cumlaude/shared-components-menu';
import { attrLabel } from '../../../services/labels';

const detailsDataCol: Attr[][] = [
	['lb_d_plaatsing_va'],
	['lb_nr_leerling'],
	['lb_fk_ll', 'll_nm_roepnaam'],
	['lb_fk_ll', 'll_nm_voorvoegsel'],
	['lb_fk_ll', 'll_nm_achternaam'],
	['lb_nm_klas'],
	['lb_nm_opleiding'],
	['lb_fk_ll', 'll_d_geboortedatum'],
	['lb_fk_pc_leerling', 'pc_nm_plaats'],
	['lb_fk_ll', 'll_nm_nationaliteit1'],
	['lb_fk_ll', 'll_nm_nationaliteit2'],
	['lb_fk_ll', 'll_nm_svh'],
	['lb_fk_ll', 'll_nm_svb'],
	['lb_fk_ll', 'll_nm_basisschooladvies_definitief'],
	['lb_fk_ll', 'll_nm_basisschooladvies_duo'],
	['lb_fk_ll', 'll_nm_basisschooladvies_herzien'],
	['lb_fk_ll', 'll_nm_basisschooladvies_oud'],
	['lb_fk_ll', 'll_nm_basisschooladvies_voorlopig'],
	['lb_d_inschrijving'],
	['lb_fk_ll', 'll_d_ned_onderwijs_sinds'],
	['lb_d_uitschrijving'],
	['lb_fk_ll', 'll_des_uitschrijving'],
	['lb_fk_ll', 'll_des_vertrekstatus'],
	['lb_fk_ll', 'll_nm_niveau_svb'],
	['lb_fk_ll', 'll_nr_leerjaar_svb'],
	['lb_fk_ll', 'll_is_svb_per_1okt'],
	['lb_nm_uitstroomniveau_vso'],
	['lb_nm_beoogd_uitstroomniveau_vso'],
	['lb_fk_ll', 'll_pic_pasfoto'],
	['lb_nm_schooljaar'],
	['lb_nr_leerjaar'],
];

const lijstDataCol: Attr[][] = [['ll_nm_achternaam'], ['ll_nm_voorvoegsel'], ['ll_nm_roepnaam'], ['ll_nr_leerling'], ['ll_pic_pasfoto']];

@Component({
	selector: 'app-leerling-details-panel',
	templateUrl: '../../base-details-panel/base-details-panel.component.html',
	styleUrls: ['../../base-details-panel/base-details-panel.component.scss'],
	imports: [
		ClickBlockingOverlayComponent,
		UserTabMenuComponent,
		TabMenuComponent,
		DashboardActiesComponent,
		InstantSearchBoxComponent,
		LijstComponent,
		DetailPanelComponent,
		SvgSpinnerComponent,
		RouterOutlet,
		AsyncPipe,
		SelectionBarComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeerlingDetailsPanelComponent extends BaseDetailsPanelComponent<Leerling, LeerlingDetail> {
	protected readonly userService = inject(UserService);

	getUrlPart(): string {
		return '/details/leerling';
	}

	getTitle(): string {
		return 'Leerlingen';
	}

	getType(): string {
		return 'leerling';
	}

	getElementKey(element: Leerling) {
		return element.ll_nr_leerling;
	}

	getLijstElementData(input: Leerling): LijstElementData {
		const title = [input.ll_nm_roepnaam, input.ll_nm_voorvoegsel, input.ll_nm_achternaam].filter(Boolean).join(' ');
		return {
			title,
			subTitle: input.ll_nr_leerling.toString(),
			pasfoto: input.ll_pic_pasfoto,
		};
	}

	getDetailsElementData(input: LeerlingDetail): DetailsElementData {
		const details = <ElementDataDetail[]>[
			{ label: attrLabel(['lb_nm_opleiding']), waarde: input.lb_nm_opleiding },
			{ label: attrLabel(['ll_d_geboortedatum']), waarde: convertDate(input.ll_d_geboortedatum) },
			{ label: attrLabel(['pc_nm_plaats']), waarde: input.pc_nm_plaats },
		];

		const nationaliteiten = [input.ll_nm_nationaliteit1, input.ll_nm_nationaliteit2].filter(Boolean);
		details.push(
			{ label: attrLabel(['ds_fun_nm_nationaliteit']), waarde: nationaliteiten.join(', ') },
			{ label: attrLabel(['ll_nm_svh']), waarde: convert(input.ll_nm_svh) }
		);

		if (!isNull(input.ll_nm_basisschooladvies_duo))
			details.push({ label: attrLabel(['ll_nm_basisschooladvies_duo']), waarde: convert(input.ll_nm_basisschooladvies_duo) });

		if (!isNull(input.ll_nm_basisschooladvies_definitief))
			details.push({ label: attrLabel(['ll_nm_basisschooladvies_definitief']), waarde: input.ll_nm_basisschooladvies_definitief });

		if (!isNull(input.ll_nm_basisschooladvies_voorlopig))
			details.push({ label: attrLabel(['ll_nm_basisschooladvies_voorlopig']), waarde: input.ll_nm_basisschooladvies_voorlopig });

		if (!isNull(input.ll_nm_basisschooladvies_oud))
			details.push({ label: attrLabel(['ll_nm_basisschooladvies_oud']), waarde: input.ll_nm_basisschooladvies_oud });

		if (!isNull(input.ll_nm_basisschooladvies_herzien))
			details.push({ label: attrLabel(['ll_nm_basisschooladvies_herzien']), waarde: input.ll_nm_basisschooladvies_herzien });

		details.push({ label: attrLabel(['lb_d_inschrijving']), waarde: convertDate(input.lb_d_inschrijving) });

		if (!isNull(input.ll_d_ned_onderwijs_sinds))
			details.push({ label: attrLabel(['ll_d_ned_onderwijs_sinds']), waarde: convertDate(input.ll_d_ned_onderwijs_sinds) });

		const datumUitschrijving = convertDate(input.lb_d_uitschrijving);
		if (datumUitschrijving != '-') {
			details.push(
				{ label: attrLabel(['lb_d_uitschrijving']), waarde: datumUitschrijving },
				{ label: attrLabel(['ll_des_uitschrijving']), waarde: convert(input.ll_des_uitschrijving) },
				{ label: attrLabel(['ll_des_vertrekstatus']), waarde: convert(input.ll_des_vertrekstatus) }
			);

			const llNmSvb = input.ll_nm_svb;
			if (llNmSvb) {
				details.push({
					label: attrLabel(['ll_nm_svb']),
					waarde: convert(llNmSvb),
				});
			}

			const per1Okt = input.ll_is_svb_per_1okt === 1 ? ' (per 1/10)' : '';
			if (input.ll_nm_niveau_svb)
				details.push({ label: attrLabel(['ll_nm_niveau_svb']) + per1Okt, waarde: input.ll_nm_niveau_svb });
			if (input.ll_nr_leerjaar_svb)
				details.push({ label: attrLabel(['ll_nr_leerjaar_svb']) + per1Okt, waarde: input.ll_nr_leerjaar_svb });
		}

		if (input.lb_nm_beoogd_uitstroomniveau_vso)
			details.push({ label: attrLabel(['lb_nm_beoogd_uitstroomniveau_vso']), waarde: input.lb_nm_beoogd_uitstroomniveau_vso });

		if (input.lb_nm_uitstroomniveau_vso)
			details.push({ label: attrLabel(['lb_nm_uitstroomniveau_vso']), waarde: input.lb_nm_uitstroomniveau_vso });

		const title = [input.ll_nm_roepnaam, input.ll_nm_voorvoegsel, input.ll_nm_achternaam].filter(Boolean).join(' ');
		return {
			title,
			subTitle: [input.lb_nr_leerling.toString(), input.lb_nm_klas],
			pasfoto: input.ll_pic_pasfoto,
			details,
		};
	}

	getLijstData(options: Partial<ListOptions>): Observable<TableResponse<Leerling>> {
		return this.dataService.getLeerlingen<Leerling>({ ...options, col: lijstDataCol });
	}

	getDetailsData(key: number): Observable<TableResponse<LeerlingDetail>> {
		return this.dataService.getLeerlingDetails<LeerlingDetail>(detailsDataCol, key);
	}

	getFilters(searchInput: string): FilterExpression | undefined {
		if (searchInput === '') return undefined;

		// Als we meerdere nummers herkennen in de input, zoek dan alleen op exact nummer. Anders op (een deel van) nummer of naam.
		const nummers = parseSearchInputToNumbers(searchInput);
		if (nummers.length > 1) {
			return new BasicFilterExpression(['ll_nr_leerling'], nummers, 'in');
		}

		return new CompoundFilterExpression(
			[
				new BasicFilterExpression(['ll_nr_leerling'], `%${searchInput}%`, 'like'),
				new BasicFilterExpression(['ll_fun_volledige_naam'], `%${searchInput}%`, 'like_unaccent'),
			],
			'or'
		);
	}

	isSelectionVisible() {
		return true;
	}
}
